export interface PlatformDocSection {
  id: string;
  title: string;
  group: string;
  content: string;
}

export const platformSections: PlatformDocSection[] = [
  // ═══════════════════════════════════════════
  //  GETTING STARTED
  // ═══════════════════════════════════════════
  {
    id: "welcome-to-skillzen",
    title: "Welcome to Skillzen",
    group: "Getting Started",
    content: `
      <div class="sz-callout sz-callout-info">
        <p><strong>Skillzen</strong> is your organization's learning platform. Here you can discover courses, earn certifications, follow learning paths, and track your progress — all in one place. Whether you're onboarding, building new skills, or preparing for a certification exam, Skillzen has you covered.</p>
      </div>

      <h3>What can you do on Skillzen?</h3>
      <ul class="sz-list">
        <li><strong>Browse and start in courses</strong> — Find training relevant to your role and interests.</li>
        <li><strong>Follow learning paths</strong> — Complete structured course sequences designed for specific goals.</li>
        <li><strong>Take exams and earn certifications</strong> — Prove your skills and download verifiable certificates.</li>
        <li><strong>Use AI Learn</strong> — Get instant help with course material from an AI study assistant.</li>
        <li><strong>Earn badges and climb leaderboards</strong> — Stay motivated with achievements and friendly competition.</li>
        <li><strong>Join forums and study groups</strong> — Connect with fellow learners and share knowledge.</li>
      </ul>

      <h3>Logging in</h3>
      <div class="sz-steps">
        <div class="sz-step">
          <div class="sz-step-num">1</div>
          <div>
            <div class="sz-step-title">Open your Skillzen URL</div>
            <div class="sz-step-desc">Your organization will provide you with a link (e.g., <code>yourcompany.skillzen.ai</code>). Open it in any modern web browser.</div>
          </div>
        </div>
        <div class="sz-step">
          <div class="sz-step-num">2</div>
          <div>
            <div class="sz-step-title">Enter your credentials</div>
            <div class="sz-step-desc">Type your email and password, then click <strong>Sign In</strong>. If your organization uses Single Sign-On (SSO), click the SSO button to log in with your corporate credentials.</div>
          </div>
        </div>
        <div class="sz-step">
          <div class="sz-step-num">3</div>
          <div>
            <div class="sz-step-title">You're in!</div>
            <div class="sz-step-desc">After signing in you'll land on your personal dashboard, where you can see your enrolled courses, upcoming deadlines, and recent activity.</div>
          </div>
        </div>
      </div>

      <div class="sz-callout sz-callout-tip">
        <div class="sz-callout-label">Tip</div>
        <p>Bookmark your Skillzen URL for quick access. If you forget your password, click <strong>Forgot Password</strong> on the login page to reset it via email.</p>
      </div>
    `,
  },
  {
    id: "your-dashboard",
    title: "Your Dashboard",
    group: "Getting Started",
    content: `
      <p>Your dashboard is the first thing you see after logging in. It's your personal learning hub — showing everything you need at a glance.</p>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-dev/a4fee313-7124-49e3-83ae-f974fd94b0a8.png" alt="Learner Dashboard" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Learner dashboard overview</p></div>

      <h3>What's on your dashboard?</h3>
      <ul class="sz-list">
        <li><strong>My Courses</strong> — Courses you're currently enrolled in, with progress bars showing how far along you are.</li>
        <li><strong>Continue Learning</strong> — A quick-access button to jump right back into the course you were last working on.</li>
        <li><strong>Upcoming Deadlines</strong> — Any courses or exams with due dates approaching.</li>
        <li><strong>Recent Achievements</strong> — Badges and certifications you've recently earned.</li>
        <li><strong>Learning Stats</strong> — A summary of your total courses completed, certifications earned, and time spent learning.</li>
      </ul>

      <div class="sz-callout sz-callout-tip">
        <div class="sz-callout-label">Tip</div>
        <p>Check your dashboard regularly to stay on top of deadlines and discover new courses. The <strong>Continue Learning</strong> button takes you straight back to where you left off — no searching required.</p>
      </div>
    `,
  },

  // ═══════════════════════════════════════════
  //  COURSES & LEARNING
  // ═══════════════════════════════════════════
  {
    id: "browsing-courses",
    title: "Browsing Courses",
    group: "Courses & Learning",
    content: `
      <p>The Course Catalog is where you discover all the training available to you. Browse by courses.</p>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/f15cbaab-dde0-481d-a1f8-1b05c99cb458.png" alt="My Courses" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: My courses list view</p></div>

      <h3>Finding courses</h3>
      <div class="sz-steps">
        <div class="sz-step">
          <div class="sz-step-num">1</div>
          <div>
            <div class="sz-step-title">Open the Course Catalog</div>
            <div class="sz-step-desc">Click <strong>Courses</strong> in the sidebar menu to open the catalog. You'll see a grid of course cards showing title, duration, difficulty, and a short description.</div>
          </div>
        </div>
        <div class="sz-step">
          <div class="sz-step-num">2</div>
          <div>
            <div class="sz-step-title">Search and filter</div>
            <div class="sz-step-desc">Use the <strong>search bar</strong> at the top to find courses by name or topic.Switch between <strong>card view</strong> and <strong>list view</strong> using the toggle icons.</div>
          </div>
        </div>
        <div class="sz-step">
          <div class="sz-step-num">3</div>
          <div>
            <div class="sz-step-title">Enroll in a course</div>
            <div class="sz-step-desc">Click on view in any course card to see its details — including the full description, module outline, estimated duration, and instructor info. Click <strong>Start</strong> to add it to your learning dashboard.</div>
          </div>
        </div>
      </div>

    `,
  },
  {
    id: "taking-a-course",
    title: "Taking a Course",
    group: "Courses & Learning",
    content: `
      <p>Once a course has been assigned to you by your admin, it's time to start learning. The course player guides you through modules and lessons at your own pace.</p>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/b3fe1a95-547b-44c9-9e28-37c877ef7c1b.png" alt="Course Contents" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Course content player</p></div>

      <h3>How the course player works</h3>
      <div class="sz-steps">
        <div class="sz-step">
          <div class="sz-step-num">1</div>
          <div>
            <div class="sz-step-title">Open your course</div>
            <div class="sz-step-desc">From your dashboard or the Course Catalog, click on an enrolled course to open the course player. You'll see the lesson content in the main area and a <strong>module sidebar</strong> on the left listing all modules and lessons.</div>
          </div>
        </div>
        <div class="sz-step">
          <div class="sz-step-num">2</div>
          <div>
            <div class="sz-step-title">Work through lessons</div>
            <div class="sz-step-desc">Each course is divided into <strong>modules</strong>, and each module contains one or more <strong>lessons</strong>. Lessons can include text, videos, documents, quizzes, or interactive content. Complete each lesson to move forward — your progress is saved automatically.</div>
          </div>
        </div>
        <div class="sz-step">
          <div class="sz-step-num">3</div>
          <div>
            <div class="sz-step-title">Track your progress</div>
            <div class="sz-step-desc">A <strong>progress bar</strong> at the top shows your overall completion percentage. Completed lessons are marked with a checkmark in the sidebar. You can revisit any completed lesson at any time.</div>
          </div>
        </div>
        <div class="sz-step">
          <div class="sz-step-num">4</div>
          <div>
            <div class="sz-step-title">Complete the course</div>
            <div class="sz-step-desc">Once you finish all required lessons the course is marked as <strong>Completed</strong>. If the course has an auto-certificate, you'll receive it immediately. The completion appears on your dashboard and profile.</div>
          </div>
        </div>
      </div>

      <div class="sz-callout sz-callout-info">
        <p>Courses are <strong>self-paced</strong> — there's no time pressure. You can pause and come back anytime. Skillzen remembers exactly where you left off.</p>
      </div>
    `,
  },
  {
    id: "course-reviews",
    title: "Course Reviews",
    group: "Courses & Learning",
    content: `
      <p>After completing a course, you can leave a review to share your experience and help other learners decide what to take next.</p>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/22e084f7-027d-4b4c-9649-2ce168fd79c6.png" alt="Course Reviews" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Course reviews and ratings</p></div>

      <h3>How reviews work</h3>
      <ul class="sz-list">
        <li><strong>Rate the course</strong> — Give the course a star rating (1–5 stars) based on your overall experience. This helps other learners quickly gauge course quality.</li>
        <li><strong>Write a review</strong> — Share what you liked, what could be improved, and any tips for future learners. Detailed reviews are the most helpful.</li>
        <li><strong>Read others' reviews</strong> — Check the reviews section to see what other learners thought. You can sort reviews by most recent or most helpful.</li>
        <li><strong>Helpful votes</strong> — If you find a review useful, give it a thumbs up. The most helpful reviews appear at the top.</li>
      </ul>

      <div class="sz-callout sz-callout-tip">
        <div class="sz-callout-label">Tip</div>
        <p>Be specific in your reviews — mention which modules were most valuable, whether the difficulty matched your expectations, and how you're applying what you learned. Constructive feedback helps course creators improve the content for everyone.</p>
      </div>
    `,
  },
  {
    id: "my-schedule",
    title: "My Schedule",
    group: "Courses & Learning",
    content: `
      <p>The Schedule page shows your upcoming deadlines, today's schedule, and overdue assignments all in one place.</p>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/a9cb377e-2e36-498b-9dfe-9c0d22a153a5.png" alt="Learning Schedule" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Learning schedule calendar view</p></div>

      <h3>What's on your schedule?</h3>
      <ul class="sz-list">
        <li><strong>Course deadlines</strong> — Any courses with due dates assigned by your admin appear here so you never miss a deadline.</li>
        <li><strong>Certification deadlines</strong> — Certification assignments with due dates are also shown, so you can track exam preparation timelines.</li>
        <li><strong>Today's schedule</strong> — See all courses and certifications scheduled for today at a glance.</li>
        <li><strong>Overdue assignments</strong> — Any courses or certifications past their due date are highlighted so you can prioritize catching up.</li>
      </ul>

      <h3>Managing your schedule</h3>
      <div class="sz-steps">
        <div class="sz-step">
          <div class="sz-step-num">1</div>
          <div>
            <div class="sz-step-title">View your calendar</div>
            <div class="sz-step-desc">Navigate to <strong>Schedule</strong> from the sidebar. Switch between <strong>day</strong>, <strong>week</strong>, and <strong>month</strong> views to plan ahead.</div>
          </div>
        </div>
        <div class="sz-step">
          <div class="sz-step-num">2</div>
          <div>
            <div class="sz-step-title">Click any event for details</div>
            <div class="sz-step-desc">Click on a scheduled item to see full details — course name, time, location (or link for virtual sessions), and any preparation notes.</div>
          </div>
        </div>
        <div class="sz-step">
          <div class="sz-step-num">3</div>
          <div>
            <div class="sz-step-title">Stay on top of deadlines</div>
            <div class="sz-step-desc">Items approaching their deadline are highlighted. You'll also receive notification reminders based on your notification preferences.</div>
          </div>
        </div>
      </div>

      <div class="sz-callout sz-callout-tip">
        <div class="sz-callout-label">Tip</div>
        <p>Check your schedule at the start of each week to plan your learning time. Blocking even 30 minutes a day makes a big difference in course completion speed.</p>
      </div>
    `,
  },

  // ═══════════════════════════════════════════
  //  CERTIFICATIONS & EXAMS
  // ═══════════════════════════════════════════
  {
    id: "taking-exams",
    title: "Taking Exams to Earn Certification",
    group: "Certifications & Exams",
    content: `
      <p>Exams test your knowledge of course material. They may be standalone assessments or part of a certification program.</p>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/48cab31e-6e97-47f7-a459-ccca73a0e67f.png" alt="Exam Instructions" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Certification exam instructions and permissions</p></div>

      <h3>How to take an exam</h3>
      <div class="sz-steps">
        <div class="sz-step">
          <div class="sz-step-num">1</div>
          <div>
            <div class="sz-step-title">Review the exam details</div>
            <div class="sz-step-desc">Before starting, you'll see the exam name, number of questions, time limit (if any), passing score, and how many attempts you have. Read these carefully so you know what to expect.</div>
          </div>
        </div>
        <div class="sz-step">
          <div class="sz-step-num">2</div>
          <div>
            <div class="sz-step-title">Start the exam</div>
            <div class="sz-step-desc">Click <strong>Start Exam</strong> when you're ready. If the exam is timed, the countdown begins immediately. Questions may include multiple choice, true/false, fill-in-the-blank, or short answer.</div>
          </div>
        </div>
        <div class="sz-step">
          <div class="sz-step-num">3</div>
          <div>
            <div class="sz-step-title">Answer questions</div>
            <div class="sz-step-desc">Work through each question at your own pace (within the time limit). You can navigate between questions and change your answers before submitting. A progress indicator shows how many questions you've answered.</div>
          </div>
        </div>
        <div class="sz-step">
          <div class="sz-step-num">4</div>
          <div>
            <div class="sz-step-title">Submit and view results</div>
            <div class="sz-step-desc">When you're done, click <strong>Submit</strong>. Your exam is graded automatically. Depending on the exam settings, you may see your score and pass/fail status immediately, or results may be released later.</div>
          </div>
        </div>
      </div>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/1f740c15-be40-4573-ad17-3265bc109c2d.png" alt="Certification Exam" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Certification exam in progress</p></div>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/c4dadd4f-f98a-42cb-92bd-875f8490025f.png" alt="Course Exam" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Course exam from learner view</p></div>

      <div class="sz-callout sz-callout-warning">
        <div class="sz-callout-label">Warning</div>
        <p>Clicking the <strong>Exit</strong> button during an exam will <strong>abandon</strong> your attempt. Your progress will not be saved and the attempt will be marked as abandoned. Only exit if you are sure you want to leave the exam.</p>
      </div>

      <div class="sz-callout sz-callout-tip">
        <div class="sz-callout-label">Tip</div>
        <p>If the exam allows multiple attempts, don't worry if you don't pass on your first try. Review the course material and try again. Some exams show correct answers after submission to help you learn.</p>
      </div>
    `,
  },


  // ═══════════════════════════════════════════
  //  LEARNING PATHS
  // ═══════════════════════════════════════════
  {
    id: "following-learning-paths",
    title: "Following Learning Paths",
    group: "Learning Paths",
    content: `
      <p>Learning paths are curated sequences of courses designed to help you build specific skills step by step. Think of them as guided training programs.</p>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/f3fe320b-246c-418a-a663-03d55cbcaef3.png" alt="Learning Path" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Learning path with course sequence</p></div>

      <h3>How learning paths work</h3>
      <div class="sz-steps">
        <div class="sz-step">
          <div class="sz-step-num">1</div>
          <div>
            <div class="sz-step-title">Find and enroll in a learning path</div>
            <div class="sz-step-desc">Browse available learning paths from the <strong>Learning Paths</strong> section in the sidebar. Each path shows the courses included, total estimated duration, and the skill you'll build. Click <strong>Start</strong> to get started.</div>
          </div>
        </div>
        <div class="sz-step">
          <div class="sz-step-num">2</div>
          <div>
            <div class="sz-step-title">Complete courses in order</div>
            <div class="sz-step-desc">Courses in a learning path are arranged in a specific sequence. Complete each course before moving to the next. Some paths have milestones that mark your progression through different phases (e.g., Beginner, Intermediate, Advanced).</div>
          </div>
        </div>
        <div class="sz-step">
          <div class="sz-step-num">3</div>
          <div>
            <div class="sz-step-title">Track your progress</div>
            <div class="sz-step-desc">The path overview shows a visual timeline of all courses with your progress. Completed courses get a checkmark, your current course is highlighted, and upcoming courses show what's next. Your overall path completion percentage is displayed at the top.</div>
          </div>
        </div>
        <div class="sz-step">
          <div class="sz-step-num">4</div>
          <div>
            <div class="sz-step-title">Complete the path</div>
            <div class="sz-step-desc">Once you finish all required courses, the learning path is marked as completed. If a certificate is attached to the path, it's generated automatically and added to your profile.</div>
          </div>
        </div>
      </div>

      <div class="sz-callout sz-callout-tip">
        <div class="sz-callout-label">Tip</div>
        <p>Learning paths are the most efficient way to build new skills. Instead of picking individual courses, follow a path designed by your organization's training team — the courses are already in the right order with the right progression.</p>
      </div>
    `,
  },

  // ═══════════════════════════════════════════
  //  AI STUDY ASSISTANT
  // ═══════════════════════════════════════════
  {
    id: "using-ai-learn",
    title: "Using AI Learn",
    group: "AI Study Assistant",
    content: `
      <p><strong>AI Learn</strong> is your personal AI study assistant. It knows the content of your courses and can help you understand difficult topics, generate practice questions, and summarize lessons.</p>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/afc5bc99-be67-4b28-995e-e4e53544ee85.png" alt="AI Learn" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: AI Learn conversation panel</p></div>

      <h3>How to use AI Learn</h3>
      <div class="sz-steps">
        <div class="sz-step">
          <div class="sz-step-num">1</div>
          <div>
            <div class="sz-step-title">Open AI Learn from a course</div>
            <div class="sz-step-desc">While viewing any lesson, click the <strong>AI Learn</strong> button in the course player toolbar. A chat panel opens alongside your lesson content.</div>
          </div>
        </div>
        <div class="sz-step">
          <div class="sz-step-num">2</div>
          <div>
            <div class="sz-step-title">Ask questions in plain language</div>
            <div class="sz-step-desc">Type any question about the course material. You can ask things like:
              <ul class="sz-list">
                <li>"Summarize this lesson in 5 bullet points"</li>
                <li>"Explain [concept] in simpler terms"</li>
                <li>"Give me a real-world example of [topic]"</li>
                <li>"Quiz me on Module 3"</li>
                <li>"What's the difference between X and Y?"</li>
              </ul>
            </div>
          </div>
        </div>
        <div class="sz-step">
          <div class="sz-step-num">3</div>
          <div>
            <div class="sz-step-title">Get course-specific answers</div>
            <div class="sz-step-desc">AI Learn responds with answers grounded in your actual course material. It cites specific lessons and modules so you can go back and review the source. Follow up with more questions to dig deeper into any topic.</div>
          </div>
        </div>
      </div>

      <div class="sz-callout sz-callout-tip">
        <div class="sz-callout-label">Tip</div>
        <p>Use AI Learn <strong>after</strong> reading the lesson, not instead of it. A great study workflow: read the lesson first, then ask AI Learn to summarize key points, and finally request practice questions to test your understanding.</p>
      </div>
    `,
  },

  // ═══════════════════════════════════════════
  //  ACHIEVEMENTS & GAMIFICATION
  // ═══════════════════════════════════════════
  {
    id: "badges-and-achievements",
    title: "Badges & Achievements",
    group: "Achievements & Gamification",
    content: `
      <p>Skillzen rewards your learning progress with badges and achievements. These are visual recognitions of your accomplishments — from completing your first course to maintaining a learning streak.</p>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/6442b18c-be37-4ed1-af11-0a0fa7aad8e4.png" alt="My Achievements" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: My achievements and badges</p></div>

      <h3>How badges work</h3>
      <ul class="sz-list">
        <li><strong>Automatic awards</strong> — Badges are earned automatically when you reach specific XP thresholds set by your admin. You earn XP by completing courses, earning certifications, or finishing learning paths.</li>
        <li><strong>Achievement gallery</strong> — View all your earned badges in your personal achievement gallery. Each badge shows what you did to earn it and when.</li>
        <li><strong>Profile showcase</strong> — Your badges appear on your profile, visible to your colleagues and managers. They're a great way to showcase your growth.</li>
        <li><strong>Progress toward badges</strong> — Each badge requires a specific XP threshold. You can see your current XP and how close you are to the next badge in the achievements section.</li>
      </ul>

      <h3>Downloading certificates</h3>
      <ul class="sz-list">
        <li><strong>Course certificates</strong> — After completing a course, you can download your course completion certificate by clicking the <strong>Download</strong> button.</li>
        <li><strong>Exam certificates</strong> — After passing a certification exam, download your certification certificate from the achievements section.</li>
        <li><strong>Learning path certificates</strong> — After completing all courses in a learning path, download your learning path completion certificate.</li>
      </ul>

      <div class="sz-callout sz-callout-info">
        <p>Badges are more than decorations — they represent real skills and dedication. Managers can view team badge summaries to recognize top performers.</p>
      </div>
    `,
  },
  {
    id: "leaderboard",
    title: "Leaderboard",
    group: "Achievements & Gamification",
    content: `
      <p>The leaderboard adds friendly competition to your learning experience. See how you rank against colleagues and stay motivated to keep learning.</p>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/550e50d8-f2b2-4cd3-964d-4f7e1f61a414.png" alt="Leaderboard" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Leaderboard page with rankings</p></div>

      <h3>How the leaderboard works</h3>
      <ul class="sz-list">
        <li><strong>Points system</strong> — You earn points for completing courses, passing exams, earning certifications, and maintaining learning streaks. The more you learn, the higher you climb.</li>
        <li><strong>Group leaderboards</strong> — Compare your progress within your team or department, not just across the entire organization.</li>
        <li><strong>Streaks</strong> — Maintain a daily or weekly learning streak to earn bonus points. The longer your streak, the bigger the multiplier.</li>
      </ul>

      <div class="sz-callout sz-callout-tip">
        <div class="sz-callout-label">Tip</div>
        <p>Even completing a single short lesson per day keeps your streak alive. Consistency matters more than marathon study sessions.</p>
      </div>
    `,
  },

  // ═══════════════════════════════════════════
  //  COMMUNICATION & SUPPORT
  // ═══════════════════════════════════════════
  {
    id: "learner-notifications",
    title: "Notifications",
    group: "Communication & Support",
    content: `
      <p>The Notifications page keeps you up to date with everything that matters — course assignments, reminders, achievements, deadlines, and announcements from your admins. Every notification you receive is listed here, and you can filter, read, and act on them from one place.</p>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/f3882468-1c4d-4981-bb34-adde31476d5a.png" alt="Learner Notifications" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Learner notifications list</p></div>

      <h3>How you receive notifications</h3>
      <ul class="sz-list">
        <li><strong>In-App</strong> — Delivered to the bell icon in the top-right of the header. Unread messages show a red badge with the count. Click the bell to preview recent items, or open the full Notifications page for the complete list.</li>
        <li><strong>Email</strong> — Delivered to your account email immediately when the admin sends them.</li>
        <li><strong>Push</strong> — Browser or device push notifications are planned for a future release. The toggle is visible in your settings but delivery is not yet enabled.</li>
      </ul>

      <h3>Viewing and managing your notifications</h3>
      <ul class="sz-list">
        <li><strong>Channel filter</strong> — Switch between <strong>All</strong>, <strong>In-App</strong>, <strong>Email</strong>, and <strong>Push</strong> to focus on one delivery channel.</li>
        <li><strong>Unread highlight</strong> — New notifications are highlighted. Click a row, or use <strong>Mark as read</strong>, to clear it. The bell badge updates instantly across your open tabs.</li>
        <li><strong>Action links</strong> — Many notifications include a <strong>View</strong> link that opens the related course, certification, or page in a new tab.</li>
        <li><strong>Priority</strong> — Urgent and High-priority notifications are visually emphasized so you can spot them quickly.</li>
        <li><strong>Pagination</strong> — Page size can be changed (10 / 20 / 50 / 100) when your inbox grows large.</li>
      </ul>

      <h3>Notification types you'll see</h3>
      <table>
        <thead>
          <tr>
            <th>Type</th>
            <th>When you get it</th>
          </tr>
        </thead>
        <tbody>
          <tr><td><strong>Assignment</strong></td><td>A course, certification, or learning path was assigned to you.</td></tr>
          <tr><td><strong>Reminder</strong></td><td>An upcoming deadline or a course you haven't opened recently.</td></tr>
          <tr><td><strong>Deadline</strong></td><td>Training that's due today, overdue, or about to expire.</td></tr>
          <tr><td><strong>Achievement</strong></td><td>You earned a badge, completed a certification, or hit a streak milestone.</td></tr>
          <tr><td><strong>Announcement</strong></td><td>Platform-wide updates from your admin.</td></tr>
          <tr><td><strong>System</strong></td><td>Account and security events (e.g., password updates).</td></tr>
        </tbody>
      </table>

      <h3>Notification preferences</h3>
      <p>Control how you want to be notified from the <strong>Settings</strong> area of the notification center. Toggle In-App and Email independently — if Email is off, only in-app notifications are shown in your bell and inbox. Push will become available once the push milestone ships; enabling it at that time will ask for browser notification permission.</p>

      <div class="sz-callout sz-callout-tip">
        <div class="sz-callout-label">Tip</div>
        <p>Check your notifications at least once per day — deadline and assignment reminders are your best defence against overdue mandatory training.</p>
      </div>
    `,
  },
  {
    id: "forums-and-study-groups",
    title: "Forums & Study Groups",
    group: "Communication & Support",
    content: `
      <p>Connect with fellow learners through forums and study groups. Ask questions, share insights, and learn together.</p>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/5e77c086-1d54-4cac-b228-d1995a27dcde.png" alt="Forums & Study Groups" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Forums & Study Groups</p></div>

      <h3>Using forums</h3>
      <ul class="sz-list">
        <li><strong>Course forums</strong> — Each course has a dedicated discussion forum where you can ask questions about the material, share tips, and help other learners.</li>
        <li><strong>General forums</strong> — Browse organization-wide discussion topics, from career advice to study techniques.</li>
        <li><strong>Creating posts</strong> — Click <strong>New Post</strong> to start a discussion. Add a title, write your message, and select the relevant category. Other learners and instructors can reply.</li>
        <li><strong>Study groups</strong> — Join or create study groups for specific courses or topics. Study groups have their own discussion space and can coordinate learning schedules.</li>
      </ul>

      <div class="sz-callout sz-callout-tip">
        <div class="sz-callout-label">Tip</div>
        <p>Before asking a question, use the search bar to check if it's already been answered. If you find a helpful answer, give it an upvote to help other learners find it too.</p>
      </div>
    `,
  },
  {
    id: "tickets",
    title: "Tickets",
    group: "Communication & Support",
    content: `
      <p>The Tickets page lets you raise requests to your manager or admin to assign you a course, certification, or learning path. You can also raise general queries.</p>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/1557eb9a-790b-4936-af50-46a547442324.png" alt="Tickets List" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Tickets listing page</p></div>

      <div class="sz-callout sz-callout-info">
        <p>This feature must be enabled by your organization administrator. If you don't see the Tickets option, contact your admin to enable <strong>Self-Ticket Assignment</strong>.</p>
      </div>

      <h3>Ticket types</h3>
      <table>
        <thead>
          <tr>
            <th>Type</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>Course</strong></td>
            <td>Request your admin to assign you a specific course.</td>
          </tr>
          <tr>
            <td><strong>Certification</strong></td>
            <td>Request assignment to a certification exam.</td>
          </tr>
          <tr>
            <td><strong>Learning Path</strong></td>
            <td>Request assignment to a learning path.</td>
          </tr>
          <tr>
            <td><strong>General</strong></td>
            <td>Raise a general query or request with a description. The admin may suggest items in response.</td>
          </tr>
        </tbody>
      </table>

      <h3>How to create a ticket</h3>
      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/42f005de-4d6f-4542-9159-e70139e0611b.png" alt="Create Ticket" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Creating a new ticket</p></div>
      <div class="sz-steps">
        <div class="sz-step">
          <div class="sz-step-num">1</div>
          <div>
            <div class="sz-step-title">Click Create Ticket</div>
            <div class="sz-step-desc">From the Tickets page, click the <strong>Create Ticket</strong> button in the top-right corner to open the ticket creation form.</div>
          </div>
        </div>
        <div class="sz-step">
          <div class="sz-step-num">2</div>
          <div>
            <div class="sz-step-title">Select the ticket type and items</div>
            <div class="sz-step-desc">Choose the type (Course, Certification, Learning Path, or General). For specific types, select the items you want to be assigned. For General tickets, describe your request.</div>
          </div>
        </div>
        <div class="sz-step">
          <div class="sz-step-num">3</div>
          <div>
            <div class="sz-step-title">Submit and track</div>
            <div class="sz-step-desc">Submit the ticket. It will appear in your tickets list with a <strong>Pending</strong> status. Your admin will review and approve, deny, or suggest alternatives.</div>
          </div>
        </div>
      </div>

      <h3>Ticket statuses</h3>
      <table>
        <thead>
          <tr>
            <th>Status</th>
            <th>Meaning</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>Pending</strong></td>
            <td>Your request is awaiting review by an admin or manager.</td>
          </tr>
          <tr>
            <td><strong>Approved</strong></td>
            <td>Your request has been approved and the items will be assigned to you.</td>
          </tr>
          <tr>
            <td><strong>Denied</strong></td>
            <td>Your request was denied. Click on the ticket to see the reason provided by the admin.</td>
          </tr>
          <tr>
            <td><strong>Closed</strong></td>
            <td>The ticket has been resolved and closed.</td>
          </tr>
        </tbody>
      </table>

      <h3>Managing your tickets</h3>
      <ul class="sz-list">
        <li><strong>Search</strong> — Search tickets by ticket number or request content.</li>
        <li><strong>Filter by type</strong> — Filter tickets by Course, Certification, Learning Path, or General.</li>
        <li><strong>Filter by status</strong> — Filter by Pending, Approved, Denied, or Closed.</li>
        <li><strong>View details</strong> — Click on a ticket to see full details including requested items, resolution status, and admin response.</li>
        <li><strong>Chat</strong> — Use the <strong>Chat</strong> button to communicate with your admin about a specific ticket.</li>
      </ul>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/7349f8a8-6e67-4467-a34c-b5dcf5bc7e58.png" alt="Ticket Chat" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Ticket chat with admin</p></div>

      <div class="sz-callout sz-callout-tip">
        <div class="sz-callout-label">Tip</div>
        <p>Include a clear message when creating a ticket explaining why you need the course, certification, or learning path. This helps your admin process your request faster.</p>
      </div>
    `,
  },

  // ═══════════════════════════════════════════
  //  MY PROFILE & SETTINGS
  // ═══════════════════════════════════════════
  {
    id: "profile-and-settings",
    title: "Profile & Settings",
    group: "My Profile & Settings",
    content: `
      <p>Your profile page lets you manage your personal information, view your learning history, and adjust your account settings.</p>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/c79b850c-acd6-4912-9432-7cfa75b4f39c.png" alt="Profile Page" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Learner profile and settings page</p></div>

      <h3>Managing your profile</h3>
      <ul class="sz-list">
        <li><strong>Personal information</strong> — Update your name, profile photo, phone number. Click your avatar in the top-right corner and select <strong>Profile</strong> to get started.</li>
        <li><strong>Change password</strong> — Go to <strong>Settings &gt; Security</strong> to update your password. You'll need to enter your current password first.</li>
        <li><strong>Recent activity logs</strong> — View your recent activity history including login, logout, and other account events to keep track of your account usage.</li>
        <li><strong>Session management</strong> — Manage your active sessions across devices. Use <strong>Sign Out Other Devices</strong> to log out from all other devices, or <strong>Emergency Logout</strong> to sign out from all devices including the current one.</li>
      </ul>

      <div class="sz-callout sz-callout-tip">
        <div class="sz-callout-label">Tip</div>
        <p>Keep your profile up to date with your latest name, photo, and contact details so your account stays accurate.</p>
      </div>
    `,
  },
];
